import React, { useState, useEffect, useCallback } from 'react';
import PartnerLayout from '@/components/layouts/PartnerLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { usePartner, API } from '@/contexts/PartnerContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  DollarSign, Loader2, ChevronLeft, ChevronRight,
  TrendingUp, Clock, CheckCircle2, Zap, Calendar,
} from 'lucide-react';

const SOURCE_META = {
  subscription: { label: 'Monthly Fee',  color: 'text-violet-400 bg-violet-500/10 border-violet-500/20' },
  percent_fee:  { label: '% TX Fee',     color: 'text-blue-400 bg-blue-500/10 border-blue-500/20' },
  extra_tx:     { label: 'Extra TX',     color: 'text-cyan-400 bg-cyan-500/10 border-cyan-500/20' },
};

const LEVEL_STYLE = {
  1: 'text-emerald-400 bg-emerald-500/15 border-emerald-500/20',
  2: 'text-blue-400   bg-blue-500/15   border-blue-500/20',
};

const TABS = [
  { key: '',        label: 'All',               icon: DollarSign },
  { key: 'tx',      label: 'Transaction Fees',  icon: Zap },
  { key: 'monthly', label: 'Monthly Fees',      icon: Calendar },
];

export default function PartnerCommissions() {
  const { headers } = usePartner();
  const [data,       setData]       = useState([]);
  const [summary,    setSummary]    = useState({});
  const [counts,     setCounts]     = useState({ monthly: 0, tx: 0 });
  const [loading,    setLoading]    = useState(true);
  const [page,       setPage]       = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total,      setTotal]      = useState(0);
  const [activeTab,  setActiveTab]  = useState('');
  const [status,     setStatus]     = useState('');
  const limit = 50;

  const fetchComms = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/partner/commissions?page=${page}&limit=${limit}`;
      if (status)    url += `&status=${status}`;
      if (activeTab) url += `&source_group=${activeTab}`;
      const { data: res } = await axios.get(url, { headers });
      setData(res.data || []);
      setTotal(res.total || 0);
      setTotalPages(res.pages || 1);
      setSummary(res.summary || {});
      setCounts(res.counts || { monthly: 0, tx: 0 });
    } catch { toast.error('Failed to load commissions'); }
    finally { setLoading(false); }
  }, [page, status, activeTab, headers]);

  useEffect(() => { fetchComms(); }, [fetchComms]);

  const handleTabChange = (key) => { setActiveTab(key); setPage(1); };
  const handleStatusChange = (val) => { setStatus(val); setPage(1); };

  const tabCount = (key) => {
    if (key === 'tx')      return counts.tx;
    if (key === 'monthly') return counts.monthly;
    return (counts.tx || 0) + (counts.monthly || 0);
  };

  return (
    <PartnerLayout title="Commissions">
      <div className="space-y-5">

        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: 'Total Earned', value: summary.total_earned, icon: TrendingUp,  color: 'text-foreground' },
            { label: 'Pending',      value: summary.pending,      icon: Clock,        color: 'text-amber-400' },
            { label: 'Paid Out',     value: summary.paid,         icon: CheckCircle2, color: 'text-emerald-400' },
          ].map(({ label, value, icon: Icon, color }) => (
            <Card key={label}><CardContent className="p-4">
              <div className="flex justify-between mb-1">
                <span className="text-xs text-muted-foreground">{label}</span>
                <Icon className={`h-4 w-4 ${color}`} />
              </div>
              <p className={`text-xl font-bold ${color}`}>${(value || 0).toFixed(2)}</p>
            </CardContent></Card>
          ))}
        </div>

        {/* Tabs + Status Filter */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          {/* Tabs */}
          <div className="flex gap-1 p-1 bg-muted/40 rounded-lg border border-border" data-testid="commission-tabs">
            {TABS.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                data-testid={`tab-${key || 'all'}`}
                onClick={() => handleTabChange(key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-150
                  ${activeTab === key
                    ? 'bg-background text-foreground shadow-sm border border-border'
                    : 'text-muted-foreground hover:text-foreground'
                  }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {label}
                <span className={`text-[11px] px-1.5 py-0.5 rounded-full font-semibold ml-0.5
                  ${activeTab === key ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'}`}>
                  {tabCount(key)}
                </span>
              </button>
            ))}
          </div>

          {/* Status filter */}
          <select
            value={status}
            onChange={e => handleStatusChange(e.target.value)}
            data-testid="status-filter"
            className="h-9 px-3 text-sm bg-background border border-input rounded-lg focus:outline-none"
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="paid">Paid</option>
          </select>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-16">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : data.length === 0 ? (
              <div className="py-16 text-center">
                <DollarSign className="h-8 w-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm text-muted-foreground">No commissions found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="commissions-table">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Date</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Merchant</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Type</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Level</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground">Rate</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground">Amount</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {data.map((c, i) => {
                      const meta = SOURCE_META[c.source_type] || { label: c.source_type, color: 'text-muted-foreground bg-muted border-border' };
                      return (
                        <tr key={c.id || i} className="hover:bg-muted/20 transition-colors">
                          <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                            {new Date(c.created_at).toLocaleString('en-US', {
                              month: 'short', day: 'numeric', year: 'numeric',
                              hour: '2-digit', minute: '2-digit',
                            })}
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-[13px] font-medium">{c.merchant_name || '—'}</span>
                            {c.merchant_email && c.merchant_email !== '—' && (
                              <p className="text-[11px] text-muted-foreground">{c.merchant_email}</p>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded border ${meta.color}`}>
                              {meta.label}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-[11px] font-bold px-2 py-0.5 rounded border ${LEVEL_STYLE[c.level] || 'text-muted-foreground bg-muted border-border'}`}>
                              L{c.level}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right text-[13px] text-muted-foreground">{c.rate}%</td>
                          <td className="px-4 py-3 text-right font-mono font-bold text-emerald-400">
                            +${c.amount.toFixed(4)}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-[11px] px-2 py-0.5 rounded font-semibold
                              ${c.status === 'paid'
                                ? 'text-emerald-500 bg-emerald-500/10'
                                : 'text-amber-500 bg-amber-500/10'}`}>
                              {c.status}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {!loading && total > 0 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                <p className="text-xs text-muted-foreground">{total} record{total !== 1 ? 's' : ''}</p>
                {totalPages > 1 && (
                  <div className="flex gap-2 items-center">
                    <Button variant="outline" size="sm" onClick={() => setPage(p => p - 1)} disabled={page === 1}>
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-xs">Page {page} / {totalPages}</span>
                    <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page === totalPages}>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

      </div>
    </PartnerLayout>
  );
}
